import {Data, Double, Int, RecordProps} from '../util';
import {UserId} from 'records/User';
import {List, Set} from 'immutable';
import {DepartmentId} from 'records/Department';
import {DayOfWeek} from '../util/time/DayOfWeek';
import {SkillId} from 'records/Skill';
import Time from 'records/Time';
import moment, {Moment} from 'moment';
import formatSecondsShort from 'utils/formatSecondsShort';
import {formatCurrencyShort} from 'utils/formatCurrencyShort';
import {TeamId} from 'records/TeamId';

/**
 * A KPI target.
 *
 * Can be either an individual KPI, or a goal, consisting of other (non-goal) KPIs.
 */
export type KpiTargetId = number;
export interface KpiTargetInterface {
  readonly id: KpiTargetId;
  /** ID of the user who created this target. */
  readonly createdByUserId: UserId;
  /** The name of the target, e.g., "Achieve $150,000 in annual sales." */
  readonly name: string;
  /** The type of the target's value. */
  readonly numericType: KpiTargetNumericType;
  /** How frequently this target should be evaluated. */
  readonly frequency: KpiTargetFrequency;
  /** How the target continues (or not) after each evaluation. */
  readonly continuity: KpiTargetContinuity;
  /** The target to hit. */
  readonly value: Double;
  /** The target to hit. */
  readonly skillIds: Set<SkillId>;
  /** Whether to show as a skill in the 9-box chart. */
  readonly chartAsSkill: boolean;
  /** Whether to show as engagement in the 9-box chart. */
  readonly chartAsEngagement: boolean;
}
export class KpiTarget extends Data<KpiTargetInterface>({
  continuity: undefined,
  createdByUserId: undefined,
  frequency: undefined,
  id: undefined,
  name: undefined,
  numericType: undefined,
  skillIds: Set(),
  value: undefined,
  chartAsSkill: undefined,
  chartAsEngagement: undefined,
}) {
  constructor(props: RecordProps<KpiTarget>) {
    super({
      ...props,
      skillIds: Set(props?.skillIds ?? []),
    });
  }
}

export type KpiOrderBy = keyof KpiTargetInterface | 'assignments';

/** Type of a target's value. */
export enum KpiTargetNumericType {
  CURRENCY = 'CURRENCY',
  PERCENTAGE = 'PERCENTAGE',
  AMOUNT = 'AMOUNT',
  TIME = 'TIME',
}

const validCharacters = {
  // TODO only having $ here feels bad and wrong, but i dont see any browser apis
  // to check the user's currency based on locale
  [KpiTargetNumericType.CURRENCY]: {
    regex: /\$|\.|,|[0-9]/g,
    charList: ['$', '.', ','],
  },
  [KpiTargetNumericType.PERCENTAGE]: {
    regex: /%|\.|,|[0-9]/g,
    charList: ['%', '.', ','],
  },
  [KpiTargetNumericType.AMOUNT]: {
    regex: /\.|,|[0-9]/g,
    charList: ['.', ','],
  },
  [KpiTargetNumericType.TIME]: {
    regex: /:|[0-9]/g,
    charList: [':'],
  },
};

export function validateKpiValue(type: KpiTargetNumericType, value: string): string[] | undefined {
  const strippedValue = value.replaceAll(validCharacters[type].regex, '');
  if (strippedValue.length !== 0) {
    return validCharacters[type].charList;
  }
}

const USDFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});
const percentFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
});
const amountFormat = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 0,
});
export function formatKpiValue(
  type: KpiTargetNumericType,
  value: number,
  {forceHours, includeUnits}: {forceHours?: boolean; includeUnits?: boolean} = {
    forceHours: true,
    includeUnits: false,
  },
  shortCurrency = false
): string {
  switch (type) {
    case KpiTargetNumericType.TIME:
      return formatSecondsShort(value * 60, forceHours, includeUnits, false);
    case KpiTargetNumericType.CURRENCY:
      // Do we care about non USD currency?
      return shortCurrency ? formatCurrencyShort(value) : USDFormat.format(value);
    case KpiTargetNumericType.PERCENTAGE:
      return percentFormat.format(value / 100);
    case KpiTargetNumericType.AMOUNT:
      return amountFormat.format(value);
  }
}

export function exampleKpiValue(type: KpiTargetNumericType) {
  return {
    [KpiTargetNumericType.CURRENCY]: '$150,000',
    [KpiTargetNumericType.PERCENTAGE]: '75%',
    [KpiTargetNumericType.AMOUNT]: '200',
    [KpiTargetNumericType.TIME]: '01:30',
  }[type];
}

/** How frequently a target should be evaluated. */
export enum KpiTargetFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  ANNUALLY = 'ANNUALLY',
}

export function formatKpiPeriod(
  freq: KpiTargetFrequency,
  opts: {tense: 'past' | 'present' | 'future'} = {tense: 'present'}
): string {
  const prefix = opts.tense === 'past' ? 'last' : opts.tense === 'future' ? 'next' : 'this';
  switch (freq) {
    case KpiTargetFrequency.DAILY:
      if (opts.tense === 'past') return 'yesterday';
      if (opts.tense === 'future') return 'tomorrow';
      return 'today';
    case KpiTargetFrequency.WEEKLY:
      return `${prefix} week`;
    case KpiTargetFrequency.MONTHLY:
      return `${prefix} month`;
    case KpiTargetFrequency.QUARTERLY:
      return `${prefix} quarter`;
    case KpiTargetFrequency.ANNUALLY:
      return `${prefix} year`;
  }
}

/** How does the target continue after each evaluation (if at all)? */
export enum KpiTargetContinuity {
  ONGOING = 'ONGOING',
  EXPIRING = 'EXPIRING',
  ONE_TIME = 'ONE_TIME',
}

export function continuityString(continuity: KpiTargetContinuity): string {
  return {
    [KpiTargetContinuity.ONGOING]: 'Ongoing',
    [KpiTargetContinuity.EXPIRING]: 'Expiring when achieved',
    [KpiTargetContinuity.ONE_TIME]: 'Expiring after first KPI Period',
  }[continuity];
}

/**
 * A target together with its children, if any.
 */
interface KpiTargetTreeInterface {
  readonly kpiTarget: KpiTarget;
  readonly kpiChildren: List<KpiTarget>;
}
export class KpiTargetTree extends Data<KpiTargetTreeInterface>({
  kpiChildren: undefined,
  kpiTarget: undefined,
}) {
  constructor(props: RecordProps<KpiTargetTree>) {
    super({
      kpiTarget: new KpiTarget(props.kpiTarget),
      kpiChildren: List(props.kpiChildren?.map(c => new KpiTarget(c)) ?? []),
    });
  }
}

export enum KpiAssigneeType {
  USER = 'USER',
  TEAM = 'TEAM',
  DEPT = 'DEPARTMENT',
  JOB = 'JOB',
}

export enum AssignmentFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export function formatAssignmentPeriod(
  frequency: AssignmentFrequency,
  opts: {tense: 'past' | 'present' | 'future'} = {tense: 'present'}
): string {
  // Build out unique impl if overlap ceases
  return formatKpiPeriod(frequency as any, opts);
}

/**
 * Returns true is p1 is shorter than p2
 */
export function periodIsShorter(p1, p2) {
  const periods = Object.values(KpiTargetFrequency);
  return periods.findIndex(f => f === p1) <= periods.findIndex(f => f === p2);
}

export interface KpiReminder {
  frequency: AssignmentFrequency;
  dayOfWeek?: DayOfWeek;
  dayOfMonth?: Int;
  options: KpiAssignmentReminderOptions;
  timeOfDay: Time;
  askViaSlack: boolean;
  askViaDashboard: boolean;
  askViaCoaching: boolean;
}

export interface KpiAssignee {
  /** id of the assignee. */
  id: DepartmentId | JobId | UserId | TeamId;
  /** Name of the assignee.
   * note: this isn't set via create or update. It's just the relevant name from the assignee
   */
  name: string;
  /** Type of the assignee. */
  type: KpiAssigneeType;
}

export type KpiAssignmentId = Int;
interface KpiAssignmentInterface {
  readonly id: KpiAssignmentId;
  readonly kpiId: KpiTargetId;
  readonly assignee: KpiAssignee;
  readonly reminder: KpiReminder;
  readonly forceComplete: boolean;
}

/**
 * Assigns a KPI target to a user or a group of users.
 *
 * KPI targets can be assigned to exactly one of:
 *  1. An individual user,
 *  2. A team,
 *  3. A department,
 *  4. A job -- the target applies to all users having that job.
 */
export class KpiAssignment extends Data<KpiAssignmentInterface>({
  assignee: undefined,
  id: undefined,
  kpiId: undefined,
  reminder: undefined,
  forceComplete: undefined,
}) {
  constructor(props: RecordProps<KpiAssignment>) {
    super({
      ...props,
      reminder: {
        ...props.reminder,
        reminderTimeOfDay: new Time(props.reminder.timeOfDay),
      },
    });
  }
}

export interface KpiAssignmentReminderOptions {
  readonly requestTemplate: string;
  readonly initialTemplate: string;
}

export type JobId = string;
/**
 * A job a user may have (e.g., SDR or AE).
 */
export interface Job {
  readonly id: JobId;
  readonly name: string;
  readonly numUsers?: number;
}

export type ModifyJobRequest = Pick<Job, 'name'>;

export interface KpiRequest {
  template: string;
  id: KpiTargetId;
  askViaDashboard: boolean;
}

export type KpiObservationId = Int;
interface KpiObservationInterface {
  id: KpiObservationId;
  name: string;
  value: number;
  numericType: KpiTargetNumericType;
  created: Moment;
  kpiId: KpiTargetId;
  observedByUserId: UserId;
}

export class KpiObservation extends Data<KpiObservationInterface>({
  created: undefined,
  id: undefined,
  name: undefined,
  numericType: undefined,
  value: undefined,
  kpiId: undefined,
  observedByUserId: undefined,
}) {
  constructor(obj: RecordProps<KpiObservationInterface>) {
    super({
      ...obj,
      created: moment(obj.created),
    });
  }
}

export interface KpiChartOption {
  name: string;
  kpiId: KpiTargetId;
  color: string;
  numericType: KpiTargetNumericType;
  group: string;
  goal: number;
  // Number of observations for this KPI
  obsCount: Int;
}

interface CoachingKpiInterface {
  goal: string;
  kpiId: KpiTargetId;
  numericType: KpiTargetNumericType;
  lastObservation?: KpiObservation;
  beforeLastObservation?: KpiObservation;
  skillRelated: boolean;
}
export class CoachingKpi extends Data<CoachingKpiInterface>({
  skillRelated: undefined,
  beforeLastObservation: undefined,
  goal: undefined,
  kpiId: undefined,
  lastObservation: undefined,
  numericType: undefined,
}) {
  constructor(props: RecordProps<CoachingKpiInterface>) {
    super({
      ...props,
      lastObservation: props.lastObservation && new KpiObservation(props.lastObservation),
      beforeLastObservation:
        props.beforeLastObservation && new KpiObservation(props.beforeLastObservation),
    });
  }
}
